import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { apiService } from '../services/api';
import type { SongComment } from '../types/comment';
import { useGameVersion } from './GameVersionContext';
import { useAuth } from './AuthContext';

interface CommentContextType {
  comments: Map<string, SongComment>;
  songIdsWithComments: Set<number>;
  isLoading: boolean;
  getComment: (songId: number, difficulty: string) => SongComment | undefined;
  hasComment: (songId: number, difficulty: string) => boolean;
  hasSongComment: (songId: number) => boolean;
  saveComment: (songId: number, difficulty: string, comment: string) => Promise<void>;
  deleteComment: (songId: number, difficulty: string) => Promise<void>;
  refreshComments: () => Promise<void>;
}

const CommentContext = createContext<CommentContextType | undefined>(undefined);

export const useComments = () => {
  const context = useContext(CommentContext);
  if (!context) {
    throw new Error('useComments must be used within a CommentProvider');
  }
  return context;
};

interface CommentProviderProps {
  children: ReactNode;
}

// Map full difficulty names to short codes
const difficultyToShortCode: Record<string, string> = {
  'NOVICE': 'NOV',
  'ADVANCED': 'ADV',
  'EXHAUST': 'EXH',
  'MAXIMUM': 'MXM',
  'INFINITE': 'INF',
  'GRAVITY': 'GRV',
  'HEAVENLY': 'HVN',
  'VIVID': 'VVD',
  'EXCEED': 'XCD',
  'ULTIMATE': 'ULT',
  'NABLA': 'NBL',
  // Already short codes
  'NOV': 'NOV',
  'ADV': 'ADV',
  'EXH': 'EXH',
  'MXM': 'MXM',
  'INF': 'INF',
  'GRV': 'GRV',
  'HVN': 'HVN',
  'VVD': 'VVD',
  'XCD': 'XCD',
  'ULT': 'ULT',
  'NBL': 'NBL',
};

const normalizeDifficulty = (diff: string): string => {
  const upper = diff.toUpperCase();
  return difficultyToShortCode[upper] || upper;
};

const makeKey = (songId: number, difficulty: string) => `${songId}-${normalizeDifficulty(difficulty)}`;

export const CommentProvider: React.FC<CommentProviderProps> = ({ children }) => {
  const [comments, setComments] = useState<Map<string, SongComment>>(new Map());
  const [songIdsWithComments, setSongIdsWithComments] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const { gameVersion } = useGameVersion();
  const { isAuthenticated } = useAuth();

  const refreshComments = useCallback(async () => {
    // Only load comments for v7
    if (gameVersion !== '7' || !isAuthenticated) {
      setComments(new Map());
      setSongIdsWithComments(new Set());
      return;
    }

    setIsLoading(true);
    try {
      const [allComments, songIds] = await Promise.all([
        apiService.getComments(),
        apiService.getSongIdsWithComments()
      ]);

      const commentsMap = new Map<string, SongComment>();
      allComments.forEach(comment => {
        commentsMap.set(makeKey(comment.songId, comment.difficulty), comment);
      });

      setComments(commentsMap);
      setSongIdsWithComments(new Set(songIds));
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [gameVersion, isAuthenticated]);

  // Load comments when version changes to v7 or auth changes
  useEffect(() => {
    refreshComments();
  }, [refreshComments]);

  const getComment = useCallback((songId: number, difficulty: string): SongComment | undefined => {
    return comments.get(makeKey(songId, difficulty));
  }, [comments]);

  const hasComment = useCallback((songId: number, difficulty: string): boolean => {
    return comments.has(makeKey(songId, difficulty));
  }, [comments]);

  const hasSongComment = useCallback((songId: number): boolean => {
    return songIdsWithComments.has(songId);
  }, [songIdsWithComments]);

  const saveComment = useCallback(async (songId: number, difficulty: string, comment: string) => {
    if (gameVersion !== '7') return;

    const savedComment = await apiService.saveComment(songId, difficulty, comment);

    setComments(prev => {
      const next = new Map(prev);
      next.set(makeKey(songId, difficulty), savedComment);
      return next;
    });

    setSongIdsWithComments(prev => {
      const next = new Set(prev);
      next.add(songId);
      return next;
    });
  }, [gameVersion]);

  const deleteComment = useCallback(async (songId: number, difficulty: string) => {
    if (gameVersion !== '7') return;

    await apiService.deleteComment(songId, difficulty);

    setComments(prev => {
      const next = new Map(prev);
      next.delete(makeKey(songId, difficulty));
      return next;
    });

    // Check if this song still has any comments
    setComments(prev => {
      const hasOtherComments = Array.from(prev.keys()).some(key => key.startsWith(`${songId}-`));
      if (!hasOtherComments) {
        setSongIdsWithComments(prevIds => {
          const next = new Set(prevIds);
          next.delete(songId);
          return next;
        });
      }
      return prev;
    });
  }, [gameVersion]);

  const value: CommentContextType = {
    comments,
    songIdsWithComments,
    isLoading,
    getComment,
    hasComment,
    hasSongComment,
    saveComment,
    deleteComment,
    refreshComments
  };

  return (
    <CommentContext.Provider value={value}>
      {children}
    </CommentContext.Provider>
  );
};
